import { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  LocalHospital as HospitalIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { diagnosticCentersService } from './diagnosticCenters.service';
import { DiagnosticCenter } from './diagnosticCenters.dto';
import { useAuth } from '../../contexts/AuthContext';

const DiagnosticCenters = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading, logout } = useAuth();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [centers, setCenters] = useState<DiagnosticCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCenters, setTotalCenters] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [centerToDelete, setCenterToDelete] = useState<number | null>(null);
  const itemsPerPage = 10;

  // Set up logout callback for session expired
  useEffect(() => {
    diagnosticCentersService.setLogoutCallback(logout);
  }, [logout]);

  // Debounce search term
  useEffect(() => {
    console.log('🔍 Search term changed to:', searchTerm);
    const timer = setTimeout(() => {
      console.log(
        '⏱️ Debounce complete - setting debouncedSearchTerm to:',
        searchTerm
      );
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      console.log('🧹 Clearing debounce timeout');
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Reset page when search term changes
  useEffect(() => {
    console.log('📄 Debounced search term changed, resetting page to 1');
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Fetch diagnostic centers from API
  const fetchCenters = useCallback(async () => {
    if (!token || !isAuthenticated) {
      setLoading(false);
      return;
    }

    console.log(
      '🔄 Fetching centers - Page:',
      currentPage,
      'Search:',
      debouncedSearchTerm
    );

    try {
      setLoading(true);
      setError(null);

      const response = await diagnosticCentersService.getDiagnosticCenters(
        token,
        {
          page: currentPage,
          limit: itemsPerPage,
          search: debouncedSearchTerm || undefined,
        }
      );

      setCenters(response.data || []);
      setTotalPages(response.meta?.lastPage || 1);
      setTotalCenters(response.meta?.total || 0);
    } catch (err) {
      console.error('Error fetching diagnostic centers:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch diagnostic centers'
      );
      setCenters([]);
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated, currentPage, debouncedSearchTerm, itemsPerPage]);

  useEffect(() => {
    fetchCenters();
  }, [fetchCenters]);

  // Handlers
  const handleDeleteClick = (id: number) => {
    setCenterToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!token || !centerToDelete) return;

    try {
      await diagnosticCentersService.deleteDiagnosticCenter(
        token,
        centerToDelete
      );
      showSnackbar('Diagnostic center deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setCenterToDelete(null);

      // If current page becomes empty after delete, go to previous page
      if (centers.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchCenters();
      }
    } catch (err) {
      console.error('Error deleting diagnostic center:', err);
      showSnackbar(
        err instanceof Error
          ? err.message
          : 'Failed to delete diagnostic center',
        'error'
      );
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCenterToDelete(null);
  };

  const handleEdit = (id: number) => {
    navigate(`/diagnostic-centers/edit/${id}`);
  };

  const handleAddCenter = () => {
    navigate('/diagnostic-centers/add');
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    console.log(
      '📄 Pagination clicked - Changing page from',
      currentPage,
      'to',
      page
    );
    setCurrentPage(page);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' = 'success'
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Loading state
  if (authLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Debug pagination state
  console.log(
    '🔢 Pagination State - loading:',
    loading,
    'totalPages:',
    totalPages,
    'currentPage:',
    currentPage,
    'totalCenters:',
    totalCenters
  );

  // Render
  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h4">Diagnostic Centers</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCenter}
          disabled={!isAuthenticated}
        >
          Add Diagnostic Center
        </Button>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by name, email, phone, city..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            ),
            endAdornment: (
              <Box display="flex" alignItems="center" gap={1}>
                {searchTerm !== debouncedSearchTerm && (
                  <CircularProgress size={20} />
                )}
                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    disabled={loading}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ),
          }}
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Centers Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading diagnostic centers...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : centers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm
                        ? 'No diagnostic centers found matching your search'
                        : 'No diagnostic centers found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                centers.map((center, index) => (
                  <TableRow key={center.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {(currentPage - 1) * 10 + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {center.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{center.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{center.phone}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{center.city}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{center.country}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={center.status === 1 ? 'Active' : 'Inactive'}
                        color={center.status === 1 ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleEdit(center.id)}
                          size="small"
                          disabled={loading}
                          title="Edit Center"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(center.id)}
                          size="small"
                          disabled={loading}
                          title="Delete Center"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {!loading && totalPages > 1 ? (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" mt={3}>
          <Typography variant="caption" color="text.secondary">
            {loading
              ? 'Loading...'
              : `Showing page ${currentPage} of ${totalPages}`}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this diagnostic center? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DiagnosticCenters;
